import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLogOut, FiLayout, FiLogIn, FiUserPlus, FiSettings, FiMenu, FiX, FiUser, FiShield } = FiIcons;

const Navbar = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      console.log('🔄 Starting logout process...');
      await logout();
      console.log('✅ Logout successful, redirecting to login...');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('❌ Failed to log out:', error);
      navigate('/login', { replace: true });
    }
  };

  const handleDashboardClick = (e) => {
    e.preventDefault();
    console.log('🔄 Navigating to dashboard...');
    
    if (location.pathname === '/dashboard') {
      window.location.reload();
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  const handleAnchorClick = (e, anchor) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(anchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(anchor);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const navLinks = [
    { label: 'What', anchor: 'what' },
    { label: 'Why', anchor: 'why' },
    { label: 'How', anchor: 'how' },
    { label: 'Features', anchor: 'features' },
    { label: 'Demo', anchor: 'demo' },
    { label: 'Pricing', anchor: 'pricing' },
    { label: 'Testimonials', anchor: 'testimonials' },
    { label: 'FAQ', anchor: 'faq' },
    { label: 'Contact', anchor: 'contact' },
  ];

  return (
    <motion.nav 
      className="bg-gray-800/50 backdrop-blur-lg border-b border-gray-700/50 sticky top-0 z-50"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 70, damping: 20 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <NavLink to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                  <SafeIcon icon={FiShield} className="text-2xl text-cyan-400" />
                </div>
              </div>
              <span className="text-xl font-black bg-gradient-to-r from-white to-cyan-400 bg-clip-text text-transparent">
                SnapChart AI
              </span>
            </NavLink>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1 flex-1 justify-center px-8">
            {navLinks.map((link) => (
              link.path ? (
                <NavLink
                  key={link.label}
                  to={link.path}
                  className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive 
                      ? 'text-cyan-400 bg-cyan-400/10' 
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </NavLink>
              ) : (
                <button
                  key={link.label}
                  onClick={(e) => handleAnchorClick(e, link.anchor)}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
                >
                  {link.label}
                </button>
              )
            ))}
          </div>
          
          {/* Auth Section */}
          <div className="hidden lg:flex items-center gap-2">
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400">
                <SafeIcon icon={FiSettings} className="animate-spin" />
                <span>Loading...</span>
              </div>
            ) : user ? (
              <>
                <button
                  onClick={handleDashboardClick}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                    location.pathname === '/dashboard' 
                      ? 'bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/50 text-cyan-400' 
                      : 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50'
                  }`}
                >
                  <SafeIcon icon={FiLayout} />
                  <span>Dashboard</span>
                </button>
                
                <button 
                  onClick={handleLogout} 
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 transition-all duration-200"
                >
                  <SafeIcon icon={FiLogOut} />
                  <span>Logout</span>
                </button>
                
                <div className="flex items-center gap-2 px-3 py-2 text-sm border-l border-gray-600 ml-2">
                  <div className="relative flex-shrink-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 rounded-lg blur-sm opacity-50"></div>
                    <div className="relative p-1.5 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                      <SafeIcon icon={FiUser} className="text-cyan-400 text-xs" />
                    </div>
                  </div>
                  <span className="text-gray-300">
                    {user.email || 'User'}
                  </span>
                </div>
              </>
            ) : (
              <>
                <NavLink 
                  to="/login" 
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 transition-all duration-200"
                >
                  <SafeIcon icon={FiLogIn} />
                  <span>Log In</span>
                </NavLink>
                
                <NavLink 
                  to="/register" 
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 transition-all duration-200"
                >
                  <SafeIcon icon={FiUserPlus} />
                  <span>Sign Up</span>
                </NavLink>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-gray-800/95 backdrop-blur-lg border-t border-gray-700/50"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                link.path ? (
                  <NavLink
                    key={link.label}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) => `block px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 ${
                      isActive 
                        ? 'text-cyan-400 bg-cyan-400/10' 
                        : 'text-gray-300 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {link.label}
                  </NavLink>
                ) : (
                  <button
                    key={link.label}
                    onClick={(e) => handleAnchorClick(e, link.anchor)}
                    className="block w-full text-left px-4 py-3 rounded-xl text-base font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
                  >
                    {link.label}
                  </button>
                )
              ))}
              
              <div className="border-t border-gray-700/50 pt-4 mt-4 space-y-2">
                {loading ? (
                  <div className="flex items-center gap-2 px-4 py-3 text-gray-400">
                    <SafeIcon icon={FiSettings} className="animate-spin" />
                    <span>Loading...</span>
                  </div>
                ) : user ? (
                  <>
                    <button
                      onClick={(e) => { handleDashboardClick(e); setMobileMenuOpen(false); }}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
                    >
                      <SafeIcon icon={FiLayout} />
                      <span>Dashboard</span>
                    </button>
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
                    >
                      <SafeIcon icon={FiLogOut} />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <>
                    <NavLink
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-semibold bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400"
                    >
                      <SafeIcon icon={FiLogIn} />
                      <span>Log In</span>
                    </NavLink>
                    <NavLink
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-semibold bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400"
                    >
                      <SafeIcon icon={FiUserPlus} />
                      <span>Sign Up</span>
                    </NavLink>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;